import { forwardRef, useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

// Modal — centered dialog primitive paired with ui/Drawer.
// Mirrors Drawer's API conventions (forwardRef, named + default export,
// body-scroll lock, Esc handler, portal usage). Visual contract follows the
// project memory: backdrop darkens via `--overlay-backdrop`, never blurs.
//
// Props:
//   open         boolean   — controls visibility
//   onClose      () => void — called on backdrop click, Esc, close button
//   title?       ReactNode — heading rendered in the header
//   description? ReactNode — secondary line below the title
//   size?        'sm' | 'md' | 'lg' | 'xl' | 'fullscreen' (default 'md')
//   footer?      ReactNode — sticky footer with right-aligned action slot
//   ariaLabel?   string    — used when there's no title
//   hideClose?   boolean   — hides the top-right X button
//   className?   string    — extra classes on the dialog panel
//   bodyClassName? string  — extra classes on the body wrapper
//   children     ReactNode — body content

// Tailwind safelist (so JIT doesn't purge): sm:max-w-[360px] sm:max-w-[520px]
// sm:max-w-[720px] sm:max-w-[960px] — sizes below match the spec exactly.
const SIZE_CLASSES = {
  sm: 'w-full sm:max-w-[360px]',
  md: 'w-full sm:max-w-[520px]',
  lg: 'w-full sm:max-w-[720px]',
  xl: 'w-full sm:max-w-[960px]',
  fullscreen: 'w-screen h-screen sm:max-w-none sm:rounded-none rounded-none',
};

// z-index: drawer is at z-[9999]. Per task spec, Modal uses 1200/1201. We use
// arbitrary tailwind values to honour those exactly — backdrop sits at 1200,
// the panel above it at 1201.

function getFocusable(root) {
  if (!root) return [];
  const selectors = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ];
  return Array.from(root.querySelectorAll(selectors.join(','))).filter(
    (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1,
  );
}

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export const Modal = forwardRef(function Modal(
  {
    open,
    onClose,
    title,
    description,
    size = 'md',
    footer,
    ariaLabel,
    hideClose = false,
    className,
    bodyClassName,
    children,
  },
  ref,
) {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const titleId = useId();
  const descId = useId();
  const reduced = useMemo(() => prefersReducedMotion(), []);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose?.();
    },
    [onClose],
  );

  // Esc + body scroll lock.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Initial focus + restore on close.
  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused =
      typeof document !== 'undefined' ? document.activeElement : null;
    // defer to next tick so the panel has mounted.
    const id = window.setTimeout(() => {
      const focusables = getFocusable(dialogRef.current);
      const target = focusables[0] || dialogRef.current;
      target?.focus?.();
    }, 0);
    return () => {
      window.clearTimeout(id);
      if (previouslyFocused && previouslyFocused.focus) {
        try {
          previouslyFocused.focus();
        } catch {
          /* ignore */
        }
      }
    };
  }, [open]);

  // Tab/Shift+Tab cycle inside the dialog (no external lib).
  const onPanelKeyDown = useCallback((e) => {
    if (e.key !== 'Tab') return;
    const focusables = getFocusable(dialogRef.current);
    if (focusables.length === 0) {
      e.preventDefault();
      dialogRef.current?.focus?.();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !dialogRef.current?.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const labelledBy = title ? titleId : undefined;
  const describedBy = description ? descId : undefined;
  const computedAriaLabel = !title ? ariaLabel : undefined;

  const node = (
    <div
      className="fixed inset-0"
      style={{ zIndex: 1200 }}
      onMouseDown={handleBackdropClick}
    >
      {/* Backdrop — uses --overlay-backdrop token, no blur. */}
      <div
        className={cn(
          'absolute inset-0',
          !reduced && 'animate-fade-in',
        )}
        style={{ background: 'var(--overlay-backdrop)' }}
        aria-hidden="true"
      />

      {/* Centered scroll container so tall panels can scroll on the page edge. */}
      <div
        className="absolute inset-0 flex items-center justify-center p-0 sm:p-4 overflow-y-auto"
        style={{ zIndex: 1201 }}
      >
        <div
          ref={(el) => {
            dialogRef.current = el;
            if (typeof ref === 'function') ref(el);
            else if (ref) ref.current = el;
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          aria-describedby={describedBy}
          aria-label={computedAriaLabel}
          tabIndex={-1}
          onKeyDown={onPanelKeyDown}
          onMouseDown={(e) => e.stopPropagation()}
          className={cn(
            'relative bg-bg text-fg shadow-elevated rounded-xl flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[90vh] overflow-hidden focus:outline-none border border-border',
            SIZE_CLASSES[size] ?? SIZE_CLASSES.md,
            !reduced && 'animate-scale-in',
            className,
          )}
        >
          {(title || description || !hideClose) && (
            <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3 shrink-0">
              <div className="min-w-0 flex-1">
                {title && (
                  <h2
                    id={titleId}
                    className="text-base font-semibold text-fg leading-tight"
                  >
                    {title}
                  </h2>
                )}
                {description && (
                  <p id={descId} className="text-sm text-fg-muted mt-0.5">
                    {description}
                  </p>
                )}
              </div>
              {!hideClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="text-fg-muted hover:text-fg p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 shrink-0"
                  aria-label={t('Cerrar')}
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          )}

          <div
            className={cn('flex-1 overflow-y-auto px-5 py-4', bodyClassName)}
          >
            {children}
          </div>

          {/* `flex-wrap`: en pantallas angostas el pie envuelve sus acciones en vez de
              desbordar el panel (el pie nunca es un scrollport horizontal). */}
          {footer && (
            <div className="px-5 py-3 border-t border-border bg-bg-muted/50 flex flex-wrap items-center justify-end gap-2 shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
});

Modal.displayName = 'Modal';

export default Modal;
